// Tysiąc / 1000 (the Polish rules, owner 2026-10-28, .ai/tysiac.md): 3 players, 24 cards (9 to ace), 7 each and 3 in
// the musik. The rank in a trick: A, 10, K, Q, J, 9; the points: A 11, 10 10, K 4, Q 3, J 2, 9 0 (120 a deal). The
// player left of the dealer must open the bidding at 100; then each raises by 10 or passes for good, at most 120
// without a marriage (K + Q of a suit) in the hand, 120 + the marriages' values with them. The declarer shows and takes
// the musik, gives 1 card to each opponent and names the contract (at least the bid); no bomb (owner 2026-10-28: the
// deal cannot be given up). The declarer leads; follow suit and beat the best card of the
// suit if you can; without the suit a trump (beating a trump in the trick if you can); else any card. A marriage is
// declared by leading its K or Q from the 2nd trick on: hearts 100, diamonds 80, clubs 60, spades 40, and that suit is
// trumps from then on. The declarer makes the contract (cards + marriages) or loses it; the others score their points
// rounded to 10. From 800 (400 in a game to 500) a seat scores only as the declarer; a seat not declaring stops at the
// target - 10. No redeal: the cards are played as dealt (owner 2026-11-02; until then four nines, or under 10 points and
// no marriage, could ask for one). The first to the target
// (500 or 1000) wins.
//
// Pure (no DOM, no React, no Math.random), as hearts/rules.ts. The cards and their helpers are Kierki's (the REAL
// suits; `fileId` maps them to the picture files). Seats: 0 the player at the bottom, 1 left, 2 right; play goes
// clockwise (0, 1, 2) and the deal moves left. NEVER compare `r` for strength: the 10 is above the K (STRENGTH).
import { shuffle } from '../engine/shuffle';
import { BASE_DECK, sameCard, type HCard, type HSuit, type Played } from '../hearts/rules';

export { cardKey, fileId, sameCard, type HCard, type HSuit, type Played } from '../hearts/rules';

export const SEATS = 3;
export const HAND = 7;
/** The 24 cards of the game, in the order of Kierki's deck (the shuffle is done on the 52, see `dealCards`). */
export const DECK: readonly HCard[] = BASE_DECK.filter((c) => c.r >= 9);
/** The games offered before the game (owner 2026-10-28): to 500 or to 1000. */
export const TARGETS = [500, 1000] as const;
export const DEFAULT_TARGET = 1000;
export const OPENING_BID = 100;
/** The highest bid without a marriage in the hand. */
export const PLAIN_CAP = 120;

/** The card's rank in a trick (higher takes). */
export const STRENGTH: Readonly<Record<number, number>> = { 14: 6, 10: 5, 13: 4, 12: 3, 11: 2, 9: 1 };
/** The card's points. */
export const POINTS: Readonly<Record<number, number>> = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2, 9: 0 };
/** A marriage's value. */
export const MARRIAGE: Readonly<Record<HSuit, number>> = { H: 100, D: 80, C: 60, S: 40 };
export const strength = (c: HCard): number => STRENGTH[c.r]!;
export const cardPoints = (c: HCard): number => POINTS[c.r]!;
export const sumPoints = (cards: readonly HCard[]): number => cards.reduce((v, c) => v + cardPoints(c), 0);

export type Phase = 'redeal' | 'bid' | 'musik' | 'give' | 'contract' | 'play' | 'trick-done' | 'hand-end' | 'over';
/** A seat's last word in the bidding: its bid, a pass, or nothing yet. */
export type Bid = number | 'pass' | null;

export interface Meld {
  readonly seat: number;
  readonly suit: HSuit;
  /** The trick it was declared in (0 = the first). */
  readonly trick: number;
}
export interface DealResult {
  readonly dealer: number;
  readonly declarer: number;
  /** The bid won; the contract played. */
  readonly bid: number;
  readonly contract: number;
  /** The declarer made the contract. */
  readonly made: boolean;
  /** Card points taken in tricks, and the marriages' points, per seat. */
  readonly cards: readonly number[];
  readonly melds: readonly number[];
  /** What each seat earned (rounded; the declarer's +/- contract), and what went on its total (the 800 rule, the cap). */
  readonly earned: readonly number[];
  readonly points: readonly number[];
}

export interface State1000 {
  /** The points that win (TARGETS). */
  readonly target: number;
  /** Deals played before this one (0 = the first; a redeal keeps the number). */
  readonly handNo: number;
  readonly dealer: number;
  /** The seat that opens the bidding (left of the dealer). */
  readonly opener: number;
  readonly hands: readonly (readonly HCard[])[];
  /** The 3 cards of the musik until the declarer takes them. */
  readonly musik: readonly HCard[];
  /** The musik's cards, also after they were taken. */
  readonly musikCards: readonly HCard[];
  readonly phase: Phase;
  /** The seats that may still ask for a redeal, the first to decide first. */
  readonly asking: readonly number[];
  readonly bids: readonly Bid[];
  /** The highest bid so far and who made it. */
  readonly bid: number;
  readonly bidder: number;
  /** -1 until the bidding is over. */
  readonly declarer: number;
  readonly contract: number;
  /** The cards the declarer gave: to the seat after it, then to the next. */
  readonly given: readonly HCard[];
  readonly trump: HSuit | null;
  readonly melds: readonly Meld[];
  readonly trick: readonly Played[];
  /** Whose word or card is next (-1 when no one's). */
  readonly turn: number;
  /** Who took the full trick (phase trick-done), else -1. */
  readonly winner: number;
  /** Card points taken in this deal. */
  readonly taken: readonly number[];
  /** The cards played in the finished tricks of this deal. */
  readonly gone: readonly HCard[];
  readonly scores: readonly number[];
  readonly history: readonly DealResult[];
  /** The shuffle pattern of this deal (52 numbers, engine/shuffle.ts: the 52 cards are shuffled, the 24 kept). */
  readonly pattern: readonly number[];
}

const has = (cards: readonly HCard[], c: HCard) => cards.some((x) => sameCard(x, c));
const without = (cards: readonly HCard[], out: readonly HCard[]) => cards.filter((c) => !has(out, c));
const HAND_ORDER: readonly HSuit[] = ['C', 'D', 'S', 'H'];
/** A hand in the order players hold it: clubs, diamonds, spades, hearts (colours alternate), low to high by rank in a
 *  trick; the trump suit at the right end. */
export function sortHand(h: readonly HCard[], trump: HSuit | null = null): HCard[] {
  const order = trump ? [...HAND_ORDER.filter((s) => s !== trump), trump] : HAND_ORDER;
  return h.slice().sort((a, b) => order.indexOf(a.s) - order.indexOf(b.s) || strength(a) - strength(b));
}
const lowestOf = (cards: readonly HCard[]) => cards.reduce((m, c) => (strength(c) < strength(m) ? c : m));
/** The suits of the marriages in `cards`. */
export const marriages = (cards: readonly HCard[]): HSuit[] =>
  HAND_ORDER.filter((s) => has(cards, { s, r: 13 }) && has(cards, { s, r: 12 }));
/** The highest bid or contract `cards` allow: 120, plus each marriage's value. */
export const bidCap = (cards: readonly HCard[]): number => PLAIN_CAP + marriages(cards).reduce((v, s) => v + MARRIAGE[s], 0);
/** The 800 rule: from this total a seat scores only as the declarer. */
export const barrelOf = (target: number): number => target - target / 5;
/** May this hand ask for a redeal: four nines, or under 10 points and no marriage. */
export const mayRedeal = (cards: readonly HCard[]): boolean =>
  cards.filter((c) => c.r === 9).length === 4 || (sumPoints(cards) < 10 && marriages(cards).length === 0);
/** Points rounded to 10 (5 and less down). */
export const round10 = (n: number): number => Math.floor((n + 4) / 10) * 10;
export const meldPoints = (s: Pick<State1000, 'melds'>, seat: number): number =>
  s.melds.reduce((v, m) => (m.seat === seat ? v + MARRIAGE[m.suit] : v), 0);

/** The 24 cards in the deal's order: the 52 shuffled (a 52-number pattern, as every game's), the 24 kept. */
export function dealCards(pattern: readonly number[] | null, rng: () => number): { cards: HCard[]; pattern: number[] } {
  const { order, pattern: used } = shuffle(BASE_DECK.length, pattern, rng);
  return { cards: order.map((i) => BASE_DECK[i]!).filter((c) => c.r >= 9), pattern: used };
}

/** A new game to `target`: the player opens the first bidding (the dealer is seat 2). */
export function startGame(target: number, pattern: readonly number[] | null, rng: () => number): State1000 {
  return startHand({ target, handNo: -1, dealer: 1, scores: [0, 0, 0], history: [] }, pattern, rng);
}

type Carry = Pick<State1000, 'target' | 'handNo' | 'dealer' | 'scores' | 'history'>;
/** The next deal (handNo + 1, the deal one seat on); `pattern` replays one (Retry, a report). */
export function startHand(prev: Carry, pattern: readonly number[] | null, rng: () => number): State1000 {
  return deal({ ...prev, handNo: prev.handNo + 1, dealer: (prev.dealer + 1) % SEATS }, pattern, rng);
}

/** Deals the cards for `c.dealer` (a redeal calls it again with the same dealer). */
function deal(c: Carry, pattern: readonly number[] | null, rng: () => number): State1000 {
  const { cards, pattern: used } = dealCards(pattern, rng);
  const opener = (c.dealer + 1) % SEATS;
  // from the opener round the table, 7 each; the 3 last cards are the musik
  const hands: HCard[][] = [[], [], []];
  for (let k = 0; k < SEATS; k++) hands[(opener + k) % SEATS] = sortHand(cards.slice(k * HAND, k * HAND + HAND));
  const musik = cards.slice(SEATS * HAND);
  // no redeal any more (owner 2026-11-02: "jak się karty tak wylosowały, to tak gramy"): the bidding starts at once; the
  // 'redeal' phase stays for a game stored before
  const asking: number[] = [];
  const bids: Bid[] = [null, null, null];
  bids[opener] = OPENING_BID;
  return {
    target: c.target, handNo: c.handNo, dealer: c.dealer, opener, hands, musik, musikCards: musik,
    phase: asking.length ? 'redeal' : 'bid', asking, bids, bid: OPENING_BID, bidder: opener, declarer: -1, contract: 0, given: [], trump: null,
    melds: [], trick: [], turn: asking.length ? asking[0]! : (opener + 1) % SEATS, winner: -1, taken: [0, 0, 0], gone: [],
    scores: c.scores, history: c.history, pattern: used,
  };
}

/** The first seat with the right answers: a redeal (the same dealer deals again) or not. */
export function answerRedeal(s: State1000, seat: number, yes: boolean, rng: () => number): State1000 | null {
  if (s.phase !== 'redeal' || s.asking[0] !== seat) return null;
  if (yes) return deal(s, null, rng);
  const asking = s.asking.slice(1);
  return asking.length ? { ...s, asking, turn: asking[0]! } : { ...s, asking, phase: 'bid', turn: (s.opener + 1) % SEATS };
}

/** The bids `seat` may make now (ascending; empty: only a pass). */
export function bidOptions(s: State1000, seat: number): number[] {
  if (s.phase !== 'bid' || s.turn !== seat) return [];
  const out: number[] = [];
  for (let v = s.bid + 10; v <= bidCap(s.hands[seat]!); v += 10) out.push(v);
  return out;
}

/** `seat` bids `value` or passes; with two passes the bidding is over and the musik turns up. */
export function placeBid(s: State1000, seat: number, value: number | 'pass'): State1000 | null {
  if (s.phase !== 'bid' || s.turn !== seat) return null;
  if (value !== 'pass' && !bidOptions(s, seat).includes(value)) return null;
  const bids = s.bids.map((b, i) => (i === seat ? value : b));
  const next: State1000 = value === 'pass' ? { ...s, bids } : { ...s, bids, bid: value, bidder: seat };
  const open = [0, 1, 2].filter((p) => bids[p] !== 'pass');
  if (open.length === 1) return { ...next, declarer: open[0]!, phase: 'musik', turn: -1 };
  let t = (seat + 1) % SEATS;
  while (bids[t] === 'pass') t = (t + 1) % SEATS;
  return { ...next, turn: t };
}

/** The declarer takes the musik (10 cards until it has given 2). */
export function takeMusik(s: State1000): State1000 {
  if (s.phase !== 'musik' || !s.musik.length) return s;
  const d = s.declarer;
  return { ...s, musik: [], phase: 'give', turn: d, hands: s.hands.map((h, i) => (i === d ? sortHand([...h, ...s.musik]) : h)) };
}

/** Who gets the declarer's next card. */
export const giveTo = (s: Pick<State1000, 'declarer' | 'given'>): number => (s.declarer + 1 + s.given.length) % SEATS;

/** The declarer gives `card` to `giveTo`; after the second the contract is named. */
export function giveCard(s: State1000, seat: number, card: HCard): State1000 | null {
  if (s.phase !== 'give' || seat !== s.declarer || !has(s.hands[seat]!, card)) return null;
  const to = giveTo(s);
  const hands = s.hands.map((h, i) => (i === seat ? without(h, [card]) : i === to ? sortHand([...h, card]) : h));
  const given = [...s.given, card];
  return { ...s, hands, given, ...(given.length === 2 ? { phase: 'contract' as const, contract: s.bid } : {}) };
}

/** The contracts the declarer may name (ascending: the bid up to its hand's cap). */
export function contractOptions(s: State1000): number[] {
  if (s.phase !== 'contract') return [];
  const out = [s.bid];
  for (let v = s.bid + 10; v <= bidCap(s.hands[s.declarer]!); v += 10) out.push(v);
  return out;
}

/** The declarer names the contract; the play begins, the declarer leading. */
export function setContract(s: State1000, seat: number, value: number): State1000 | null {
  if (s.phase !== 'contract' || seat !== s.declarer || !contractOptions(s).includes(value)) return null;
  return { ...s, contract: value, phase: 'play', turn: seat };
}

/** The seat whose card takes the trick: the strongest trump, else the strongest of the suit led. */
export function trickWinner(trick: readonly Played[], trump: HSuit | null): number {
  const led = trick[0]!.card.s;
  const lead = trump && trick.some((p) => p.card.s === trump) ? trump : led;
  let best: Played | null = null;
  for (const p of trick) if (p.card.s === lead && (!best || strength(p.card) > strength(best.card))) best = p;
  return best!.seat;
}

/** The cards `seat` may play now (empty when it is not its turn): the suit led, a higher one if it has one (unless a
 *  trump already took the trick); without the suit a trump, higher than the best trump in the trick if it can. */
export function legalCards(s: State1000, seat: number): HCard[] {
  if (s.phase !== 'play' || s.turn !== seat) return [];
  const hand = s.hands[seat]!;
  if (!s.trick.length) return hand.slice();
  const led = s.trick[0]!.card.s;
  const best = s.trick.find((p) => p.seat === trickWinner(s.trick, s.trump))!.card;
  const above = (cards: HCard[]) => {
    const up = cards.filter((c) => c.s === best.s && strength(c) > strength(best));
    return up.length ? up : cards;
  };
  const follow = hand.filter((c) => c.s === led);
  if (follow.length) return above(follow);
  const trumps = s.trump ? hand.filter((c) => c.s === s.trump) : [];
  if (trumps.length) return above(trumps);
  return hand.slice();
}

/** Would leading `card` now declare a marriage: from the 2nd trick on, its K or Q with the other in the hand. */
export function meldsWith(s: State1000, seat: number, card: HCard): boolean {
  if (s.phase !== 'play' || s.turn !== seat || s.trick.length || !s.gone.length) return false;
  if (card.r !== 12 && card.r !== 13) return false;
  return has(s.hands[seat]!, { s: card.s, r: card.r === 12 ? 13 : 12 });
}

/** `seat` plays `card`; null when it may not. A K or Q led from a marriage declares it (that suit is trumps). The
 *  third card fills the trick (phase trick-done, `winner` set). */
export function playCard(s: State1000, seat: number, card: HCard): State1000 | null {
  if (!legalCards(s, seat).some((c) => sameCard(c, card))) return null;
  let next: State1000 = s;
  if (meldsWith(s, seat, card)) {
    const trump = card.s;
    next = { ...s, trump, melds: [...s.melds, { seat, suit: trump, trick: s.gone.length / SEATS }], hands: s.hands.map((h) => sortHand(h, trump)) };
  }
  const hands = next.hands.map((h, i) => (i === seat ? without(h, [card]) : h));
  const trick = [...next.trick, { seat, card }];
  if (trick.length < SEATS) return { ...next, hands, trick, turn: (seat + 1) % SEATS };
  return { ...next, hands, trick, turn: -1, winner: trickWinner(trick, next.trump), phase: 'trick-done' };
}

/** The full trick goes to its winner, who leads next; after the 8th trick the deal is scored. */
export function collectTrick(s: State1000): State1000 {
  if (s.phase !== 'trick-done') return s;
  const w = s.winner;
  const taken = s.taken.map((n, i) => (i === w ? n + sumPoints(s.trick.map((p) => p.card)) : n));
  const next: State1000 = { ...s, taken, gone: [...s.gone, ...s.trick.map((p) => p.card)], trick: [], winner: -1, turn: w, phase: 'play' };
  return next.hands.every((h) => h.length === 0) ? scoreHand(next) : next;
}

function scoreHand(s: State1000): State1000 {
  const d = s.declarer;
  const melds = [0, 1, 2].map((p) => meldPoints(s, p));
  const made = s.taken[d]! + melds[d]! >= s.contract;
  const earned = [0, 1, 2].map((p) => (p === d ? (made ? s.contract : -s.contract) : round10(s.taken[p]! + melds[p]!)));
  return finish(s, { made, cards: s.taken, melds, earned });
}

/** Puts a deal's points on the totals: a seat not declaring gets nothing from the 800 rule's line on and never goes
 *  above the target - 10; the declarer reaching the target ends the game. */
function finish(s: State1000, r: Pick<DealResult, 'made' | 'cards' | 'melds' | 'earned'>): State1000 {
  const d = s.declarer;
  const barrel = barrelOf(s.target);
  const points = r.earned.map((e, p) => {
    if (p === d) return e;
    if (s.scores[p]! >= barrel) return 0;
    return Math.max(0, Math.min(e, s.target - 10 - s.scores[p]!));
  });
  const result: DealResult = { dealer: s.dealer, declarer: d, bid: s.bid, contract: s.contract, ...r, points };
  const scores = s.scores.map((v, i) => v + points[i]!);
  return { ...s, turn: -1, scores, history: [...s.history, result], phase: scores.some((v) => v >= s.target) ? 'over' : 'hand-end' };
}

/** The seats with the highest total. */
export const leaders = (scores: readonly number[]): number[] => {
  const high = Math.max(...scores);
  return scores.flatMap((v, i) => (v === high ? [i] : []));
};
/** The player won: the game is over and the player reached the target (only a declarer can). */
export const playerWon = (s: State1000): boolean => s.phase === 'over' && s.scores[0]! >= s.target;
/** The score of a won game for the Ranking: the player's total. */
export const winScore = (s: State1000): number => Math.max(0, s.scores[0]!);

// ---- the computer players (simple and fair: they see their own hand, the table and the cards gone) ----------------

/** The card points a declarer may count on with `cards` (its own and the tricks they take), with `extra` cards to come
 *  (the musik before it is seen). */
export function handValue(cards: readonly HCard[], extra = 0): number {
  let v = 0;
  for (const suit of HAND_ORDER) {
    const own = cards.filter((c) => c.s === suit);
    const ace = own.some((c) => c.r === 14), ten = own.some((c) => c.r === 10);
    if (ace) v += 11 + 8;
    if (ten) v += ace ? 10 + 6 : own.length >= 3 ? 6 : 2;
    v += own.filter((c) => c.r === 13 || c.r === 12).length * (ace ? 3 : 1);
    if (own.length >= 4) v += (own.length - 3) * 6;
  }
  const aces = cards.filter((c) => c.r === 14).length;
  // a marriage counts once a trick is taken to lead it
  v += marriages(cards).reduce((m, s) => m + MARRIAGE[s] * (aces ? 1 : 0.7), 0);
  return v + extra * 8;
}

/** A computer player's word: the next bid while its hand is worth it, else a pass. */
export function botBid(s: State1000, seat: number): number | 'pass' {
  const options = bidOptions(s, seat);
  if (!options.length) return 'pass';
  // the musik's 3 cards come and 2 go back out: a little more than the 7 cards show
  const worth = handValue(s.hands[seat]!, 1) + 15;
  return options[0]! <= worth ? options[0]! : 'pass';
}

/** A computer player with the right asks for a redeal. */
export const botRedeal = (s: State1000, seat: number): boolean => mayRedeal(s.hands[seat]!);

/** The 2 cards a computer declarer gives (the weakest, never a marriage's or an ace; low points). */
export function botGive(s: State1000, seat: number): HCard {
  const hand = s.hands[seat]!;
  const keep = new Set(marriages(hand));
  const len = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
  const cost = (c: HCard) =>
    (keep.has(c.s) && (c.r === 12 || c.r === 13) ? 1000 : 0) + (c.r === 14 ? 500 : 0) + (c.r === 10 && !has(hand, { s: c.s, r: 14 }) ? 20 : c.r === 10 ? 300 : 0) + strength(c) * 10 + len(c.s) * 3;
  return hand.slice().sort((a, b) => cost(a) - cost(b))[0]!;
}

/** The contract a computer declarer names: the bid, or more when its 8 cards are worth well more. */
export function botContract(s: State1000, seat: number): number {
  const options = contractOptions(s);
  // a strong hand takes a little less than it looks (measured over many computer games)
  const v = handValue(s.hands[seat]!);
  const worth = v <= 160 ? v - 10 : 150 + (v - 160) * 0.5;
  const fitting = options.filter((x) => x <= worth);
  return fitting[fitting.length - 1] ?? options[0]!;
}

/** The strongest card of `suit` still out (not gone, not in `own`, not on the table), or 0 when none. */
function topOut(s: State1000, own: readonly HCard[], suit: HSuit): number {
  for (const r of [14, 10, 13, 12, 11, 9]) {
    const c = { s: suit, r };
    if (!has(s.gone, c) && !has(own, c) && !has(s.trick.map((p) => p.card), c)) return STRENGTH[r]!;
  }
  return 0;
}

/** The card a computer player plays (always one of `legalCards`). The two players against the declarer help each
 *  other: points go on a trick the partner holds. */
export function botPlay(s: State1000, seat: number): HCard {
  const legal = legalCards(s, seat);
  if (!legal.length) throw new Error(`botPlay: seat ${seat} has no card to play`);
  const hand = s.hands[seat]!;
  const trump = s.trump;
  const trumpsOut = trump ? topOut(s, hand, trump) > 0 : false;
  // a card nobody can beat: the strongest of its suit still out, and no trump can take it (or it is a trump)
  const boss = (c: HCard) => strength(c) > topOut(s, hand, c.s) && (c.s === trump || !trumpsOut);
  if (!s.trick.length) {
    // a marriage first (the highest), led with its queen
    const melds = marriages(hand).filter((suit) => meldsWith(s, seat, { s: suit, r: 12 }));
    if (melds.length) return { s: melds.reduce((m, x) => (MARRIAGE[x] > MARRIAGE[m] ? x : m)), r: 12 };
    const sure = legal.filter(boss);
    if (sure.length) return sure.reduce((m, c) => (cardPoints(c) > cardPoints(m) ? c : m));
    // else a low card from the longest suit outside the trumps (not a marriage's)
    const keep = new Set(marriages(hand));
    const plain = legal.filter((c) => c.s !== trump && !(keep.has(c.s) && (c.r === 12 || c.r === 13)));
    const pool = plain.length ? plain : legal;
    const len = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
    return pool.reduce((m, c) => (cardPoints(c) < cardPoints(m) || (cardPoints(c) === cardPoints(m) && len(c.s) > len(m.s)) ? c : m));
  }
  const holder = trickWinner(s.trick, trump);
  const best = s.trick.find((p) => p.seat === holder)!.card;
  const friend = seat !== s.declarer && holder !== s.declarer;
  const last = s.trick.length === SEATS - 1;
  const beats = (c: HCard) => (c.s === best.s ? strength(c) > strength(best) : c.s === trump);
  const winners = legal.filter(beats);
  const cheapest = (cards: HCard[]) => cards.reduce((m, c) => (cardPoints(c) < cardPoints(m) || (cardPoints(c) === cardPoints(m) && strength(c) < strength(m)) ? c : m));
  const richest = (cards: HCard[]) => cards.reduce((m, c) => (cardPoints(c) > cardPoints(m) ? c : m));
  // the partner holds the trick and nobody after can take it: points on it
  const friendSafe = friend && (last || (best.s === trump ? topOut(s, hand, trump!) < strength(best) : boss(best) || strength(best) > topOut(s, hand, best.s)));
  if (friendSafe) {
    const gifts = legal.filter((c) => c.r !== 14 || !boss(c));
    return richest(gifts.length ? gifts : legal);
  }
  if (winners.length && !friend) {
    if (last) return lowestWinner(winners, s.trick[0]!.card.s);
    const sure = winners.filter(boss);
    if (sure.length) return richest(sure);
    const trickPts = sumPoints(s.trick.map((p) => p.card));
    if (trickPts >= 10) return lowestWinner(winners, s.trick[0]!.card.s);
  }
  return cheapest(legal);
}

/** The weakest card that still takes (a trump only when no card of the suit led takes). */
function lowestWinner(winners: readonly HCard[], led: HSuit): HCard {
  const suit = winners.filter((c) => c.s === led);
  return lowestOf(suit.length ? suit : winners);
}
